import { View, SxProp, styled, TextInput } from 'dripsy';
import ReactNative, { Pressable } from 'react-native';

import React, { useEffect, useRef, useState } from 'react';
import { TextCaption } from '../typography/UniTypography';
import { DropdownMenu } from './DropdownMenu';
import { useDropdown } from './useDropdown.hook';
import { Icon } from '../icons/Icon';

const PressableView = styled(Pressable)();

export interface DropdownOption {
  label: string;
  value: string;
}

export interface DropdownOptions extends Array<DropdownOption> {}

interface DropdownProps {
  onValueChange: (value: string | string[]) => void;
  onSearch?: (value: string) => void;
  options: DropdownOptions;
  value?: string | string[];
  loading?: boolean;
  multiple?: boolean;
  placeholder?: string;
  prefix?: string;
  label?: string;
  sx?: SxProp;
  dropdownSx?: SxProp;
  dropdownItemSx?: (hovered?: boolean, selected?: boolean) => SxProp;
}

export const Dropdown: React.FC<DropdownProps> = ({
  onValueChange,
  onSearch,
  options,
  value,
  multiple,
  prefix,
  placeholder,
  label,
  sx,
  dropdownSx,
  dropdownItemSx
}) => {
  const { isOpen, dropdownLayout, displayLabel, data, onToggle, onLayout, onSelect, onClose } = useDropdown({
    onValueChange,
    options,
    value,
    multiple,
    placeholder
  });

  return (
    <View>
      {label ? (
        <TextCaption semiBold label>
          {label}
        </TextCaption>
      ) : null}
      <PressableView onPress={onToggle} onLayout={onLayout} sx={{ ...styles.container, width: dropdownLayout.width, ...sx }}>
        <React.Fragment>
          <View sx={styles.left}>
            {prefix ? (
              <View sx={styles.prefix}>
                <TextCaption semiBold>{prefix}</TextCaption>
              </View>
            ) : null}
            <View sx={styles.content}>
              <DropdownInput
                value={displayLabel}
                hasFocus={isOpen}
                onDeselect={onSelect}
                displayType={multiple ? 'LABEL' : 'TEXT'}
                onSearch={onSearch}
              />
            </View>
          </View>
          <View sx={styles.suffix}>
            <Icon name={isOpen ? 'arrowUp' : 'arrowDown'} />
          </View>
          <DropdownMenu
            isOpen={isOpen}
            data={data}
            multiple={multiple}
            value={value}
            dropdownLayout={{
              top: dropdownLayout.height,
              left: 0,
              width: dropdownLayout.width
            }}
            sx={dropdownSx}
            dropDownItemSx={dropdownItemSx}
            onSelect={onSelect}
          />
        </React.Fragment>
        {/* Menu */}
      </PressableView>
    </View>
  );
};

interface InputProps {
  value?: string | string[];
  hasFocus?: boolean;
  onDeselect: (value: string) => void;
  onSearch?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  displayType?: 'TEXT' | 'LABEL';
  sx?: SxProp;
}

const DropdownInput: React.FC<InputProps> = ({ value, hasFocus, onDeselect, onSearch, onFocus, onBlur, displayType = 'TEXT', sx }) => {
  const ref = useRef<ReactNative.TextInput>(null);
  const focus = () => {
    setValue('');
    onFocus && onFocus();
  };
  const blur = () => {
    onSearch && onSearch(''); // Restore the data

    if (displayType === 'TEXT') {
      setValue(placeholder);
    } else {
      setValue('');
    }
    onBlur && onBlur();
  };
  const changeText = (text: string) => {
    setValue(text);
    onSearch && onSearch(text);
  };
  const keyPress = (event: any) => {
    if (displayType === 'LABEL' && event.key === 'Backspace' && inputValue.length === 0 && Array.isArray(value) && value.length > 0) {
      onDeselect(value.pop()!);
    }
  };

  const [placeholder, setPlaceholder] = useState<string>('');
  const [inputValue, setValue] = useState<string>('');
  useEffect(() => {
    switch (displayType) {
      case 'LABEL':
        {
          setPlaceholder('');
        }
        break;
      default: {
        const _placeholder = Array.isArray(value) ? value.join(',') : value || '';
        setPlaceholder(_placeholder);
        setValue(_placeholder);
        break;
      }
    }
  }, [value, displayType]);

  useEffect(() => {
    if (hasFocus) {
      ref.current?.focus();
    } else {
      ref.current?.blur();
    }
  }, [hasFocus, value]);

  return (
    <View sx={{ ...styles.input, ...sx, ...(displayType === 'LABEL' ? styles.inputWrap : {}) }}>
      {displayType === 'LABEL' &&
        (Array.isArray(value) ? value : [value]).map((label, i) => {
          return (
            <PressableView onPress={() => label && onDeselect(label)} key={i} sx={styles.selected}>
              <TextCaption>{label}</TextCaption>
              <Icon name="cross" width={10} height={10} />
            </PressableView>
          );
        })}
      <TextInput
        ref={ref}
        sx={{ ...styles.inputStyle, cursor: !!onSearch ? 'text' : 'pointer', width: displayType === 'LABEL' ? 80 : undefined }}
        onFocus={focus}
        onBlur={blur}
        value={inputValue}
        placeholder={placeholder}
        onChangeText={changeText}
        returnKeyType="search"
        onKeyPress={keyPress}
        editable={!!onSearch}
      />
    </View>
  );
};

enum StyleName {
  container = 'container',
  prefix = 'prefix',
  left = 'left',
  content = 'content',
  suffix = 'suffix',
  selected = 'selected',
  input = 'input',
  inputStyle = 'inputStyle',
  inputWrap = 'inputWrap'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    minHeight: '$11',
    paddingX: '$2',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '$borderField',
    borderRadius: 5,
    zIndex: 999
  },
  left: {
    flexDirection: 'row',
    flex: 1,
    gap: '$3'
  },
  prefix: {},
  content: { flexDirection: 'row', flex: 1, width: '100%', alignItems: 'center', gap: '$2' },
  suffix: {},
  selected: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$2',
    padding: '$1',
    borderRadius: 2,
    borderWidth: 1,
    borderColor: '$borderField',
    backgroundColor: '$backgroundTheme'
  },
  input: { flexDirection: 'row', gap: '$2', width: '100%' },
  inputStyle: { flex: 1, outlineStyle: 'none', fontSize: 14 },
  inputWrap: { paddingY: 6, flexFlow: 'wrap' }
};
